import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SuccessStories = () => {
  return (
    <section id="success-stories" className="py-24 bg-light relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(77,162,255,0.08)_0%,transparent_50%),radial-gradient(circle_at_70%_30%,rgba(222,97,97,0.08)_0%,transparent_50%)]"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge badge-pink inline-flex mb-4">
            <FontAwesomeIcon icon="trophy" />
            <span>Success Stories</span>
          </div>
          
          <h2 className="text-h2 mb-6">
            Real <span className="text-gradient-blue-pink">Results</span> from Real Users
          </h2>
          
          <p className="text-body-lg text-gray-600">
            Discover how professionals and businesses are transforming their networking with AM CARDMATE.
          </p>
        </div>
        
        <div className="flex flex-col lg:flex-row items-center gap-16 mt-16">
          {/* Image Section */}
          <div className="w-full lg:w-1/2 relative perspective-1000 max-w-[600px] mx-auto">
            <div className="relative">
              {/* Background Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl transform scale-105"></div>
              
              <div className="absolute inset-0 opacity-20">
                <div 
                  className="absolute inset-0 rounded-3xl"
                  style={{
                    backgroundImage: `radial-gradient(circle, #4DA2FF 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}
                />
              </div>
              
              {/* Animated Blobs */}
              <motion.div 
                className="absolute -top-10 -left-10 w-40 h-40 bg-blue-400/30 rounded-full blur-3xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <motion.div 
                className="absolute -bottom-10 -right-10 w-48 h-48 bg-purple-400/30 rounded-full blur-3xl"
                animate={{ 
                  scale: [1.2, 1, 1.2],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ 
                  duration: 5, 
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
              
              {/* Image Container */}
              <div className="relative transform-style-3d hover:rotate-y-5 hover:rotate-x-5 transition-transform duration-500 p-6">
                <div className="absolute inset-4 bg-white rounded-2xl shadow-inner"></div>
                
                <div className="relative z-10">
                  <img 
                    src="/assets/conference.jpg" 
                    alt="Success Story - Tech Conference" 
                    className="w-full rounded-2xl shadow-2xl object-cover relative z-10"
                    style={{
                      maxHeight: '400px',
                      width: '100%',
                      height: 'auto',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-white/10 to-transparent rounded-2xl pointer-events-none"></div>
                </div>
                
                {/* Stats Badge */}
                <div className="absolute -bottom-6 -right-6 w-36 h-36 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-center p-4 font-display font-bold shadow-2xl z-20">
                  <div>
                    <div className="text-h3 font-extrabold">500+</div>
                    <div className="text-body-sm">Connections Made</div>
                  </div>
                </div>
                
                {/* Floating Icons */}
                <motion.div 
                  className="absolute -top-4 right-10 bg-white rounded-full p-3 shadow-lg"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <FontAwesomeIcon icon="users" className="text-blue-500 text-xl" />
                </motion.div>
                
                <motion.div 
                  className="absolute top-10 -left-4 bg-white rounded-full p-3 shadow-lg"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <FontAwesomeIcon icon="handshake" className="text-purple-500 text-xl" />
                </motion.div>
              </div>
            </div>
          </div>
          
          {/* Content Section */}
          <div className="w-full lg:w-1/2">
            <h4 className="flex items-center gap-4 text-h5 text-primary-blue mb-6">
              <FontAwesomeIcon icon="users" />
              <span>Case Study: Annual Tech Conference</span>
            </h4>
            
            <h3 className="text-h2 text-dark leading-tight mb-8">
              How SmartTech Revolutionized Their Conference Networking
            </h3>
            
            <p className="text-body-lg text-gray-600 mb-6 leading-relaxed">
              When SmartTech decided to go digital with their networking at their annual conference, they chose AM CARDMATE. By providing each attendee with a custom NFC card, they eliminated the need for paper business cards and created a more engaging networking experience.
            </p>
            
            <p className="text-body-lg text-gray-600 mb-10 leading-relaxed">
              The result? A 78% increase in meaningful connections, simplified follow-ups, and a significant reduction in paper waste. Attendees rated the networking experience 4.8/5 stars.
            </p>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="text-gradient-blue-pink text-h2 font-display font-extrabold mb-2">78%</div>
                <div className="text-body font-semibold text-gray-dark">More Connections</div>
              </div>
              
              <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="text-gradient-blue-pink text-h2 font-display font-extrabold mb-2">94%</div>
                <div className="text-body font-semibold text-gray-dark">Satisfaction Rate</div>
              </div>
              
              <div className="text-center p-8 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <div className="text-gradient-blue-pink text-h2 font-display font-extrabold mb-2">0</div>
                <div className="text-body font-semibold text-gray-dark">Paper Cards Used</div>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-6">
              <a href="#app-download" className="btn btn-primary">
                <FontAwesomeIcon icon="lightbulb" />
                <span>Get Started Today</span>
              </a>
              <a href="#testimonials" className="btn btn-outline">Read More Stories</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;